<template>
	<v-container fluid class="relative py-0">
		<!-- create dialog -->
		<v-dialog v-model="dialog" width="500px" height="auto">
			<v-form ref="form" @submit.prevent="submitInvoice" :disabled="submitLoading">
				<v-card>
					<v-card-title>
						{{$t('invoices.add-invoice')}}
					</v-card-title>
					<v-card-text class="pb-0">
						<v-row dense>
							<!-- account -->
							<v-col cols="12" sm="6">
								<v-autocomplete
									v-model="submitData.account"
									:label="$t('inputs.account')"
									:items="configs.accounts.filter(c => c.type === 2)"
									item-value="id"
									:item-text="item => item.accounting_no + '-'+ item.name"
									outlined
									dense
									:rules="[rules.required]"
								>
									<template v-slot:selection="{ item }">
										{{ item.accounting_no +'-'+ item.name }}
									</template>
									<template v-slot:item="{ item }">
										{{ item.accounting_no + '-'+ item.name }}
										<v-spacer></v-spacer>
										<bee-advanced-money-label
											:with-color="true"
											:value="item?.account_statement_summary"
											:show-zeros-values="true"
										></bee-advanced-money-label>
									</template>
								</v-autocomplete>
							</v-col>
				
							<!-- refunded from -->
							<v-col cols="12" sm="6">
								<v-autocomplete
									v-model="submitData.refundedFrom"
									:items="saleInvoices"
									item-text="no"
									item-value="id"
									multiple
									:label="$t('inputs.refunded-from-invoice')"
									outlined
									dense
								/>
							</v-col>
				
							<!-- date -->
							<v-col cols="12" sm="6">
								<bee-date-input
									v-model="submitData.date"
									:label="$t('inputs.date')"
									dense
									clearable
									prepend-icon="mdi-calendar"
									user-menu-picker
									input-format-order="DD-MM-YYYY"
									class="w-100"
									:disabled="submitLoading"
									:rules="[rules.date]"
								></bee-date-input>
							</v-col>
				
							<!-- rfDoc -->
							<v-col cols="12" sm="6">
								<v-text-field 
									v-model="submitData.refDoc"
									:label="$t('inputs.ref-doc')"
									outlined
									dense
									:rules="[rules.required]"
								/>
							</v-col>
							
							<!-- note -->
							<v-col cols="12">
								<v-textarea
									v-model="submitData.notes"
									:label="$t('inputs.notes')"
									outlined
									auto-grow
									no-resize
									rows="2"
								/>
							</v-col>
						</v-row>
					</v-card-text>
					<v-card-actions>
						<v-btn
							type="submit"
							text
							:loading="submitLoading"
							color="success-color"
						>
							{{$t('buttons.create')}}
						</v-btn>
						<v-btn
							text
							color="danger-dark-color"
							:disabled="submitLoading"
							@click="dialog = false"
						>
							{{$t('buttons.cancel')}}
						</v-btn>
					</v-card-actions>
				</v-card>
			</v-form>
		</v-dialog>

		<!-- delete dialog -->
		<v-dialog v-model="deleteDialog" width="450px">
			<v-card>
				<v-card-title>
					{{$t('invoices.delete-invoice')}}
				</v-card-title>
				
				<v-card-text class="py-2">
					{{$t('invoices.delete-invoice-confirm')}}
				</v-card-text>

				<v-card-actions>
					<v-btn text color="success-color">{{$t('buttons.ok')}}</v-btn>
					<v-btn text color="danger-dark-color" @click="deleteDialog = false">
						{{$t('buttons.cancel')}}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- dialog delete entry -->
		<bee-dialog
			v-model="deleteDialog"
			:title="$t('invoices.delete-invoice')"
			:message="$t('invoices.delete-invoice-confirm')"
			max-width="450"
			@handler="deleteInvoice"
			@close="deleteDialog = false"
		></bee-dialog>

		<!-- dialog delete entry -->
		<bee-dialog
			v-model="dialogDeleteAccountingEntry"
			:title="$t('invoices.delete-entry')"
			:message="$t('invoices.are-you-sure-you-want-to-delete-the-entry')"
			max-width="300"
			@handler="deleteEntry()"
			@close="dialogDeleteAccountingEntry = false; deleteEntryId = null"
		></bee-dialog>
		
		<!-- title & buttons -->
		<v-row class="ma-0 align-center">
			<v-col cols="auto"  align="center" class="d-flex justify-start">
				<slot name="breadcrumbs" :item="true"></slot>
			</v-col>

			<v-col cols="auto" class="d-flex flex-row justify-start">
				<bee-bond-up-down
					v-model="invoiceNo"
					:items="saleRefundInvoices.map(c => c.no)"
					class="medium-placeholder"
					:label="$t('invoices.invoice-no')"
					@keydown-enter-prevent="invoiceNoIsChanged"
					@programmatically-changed="invoiceNoIsChanged"
				></bee-bond-up-down>
			</v-col>

			<v-col cols="auto">
				<v-btn :disabled="isViewer" color="success white--text" class="mx-2" @click="dialog = true">
					<v-icon> mdi-plus </v-icon>
					{{$t('buttons.new')}}
				</v-btn>

				<v-btn v-if="$route.params.id" :disabled="isViewer" color="danger-color" dark @click="deleteDialog = true">
					<v-icon> mdi-delete </v-icon>
					{{$t('buttons.delete')}}
				</v-btn>
			</v-col>
		</v-row>
		
		<v-row class="border-container ma-0 py-0 px-0" align="center">
			<!-- account -->
			<v-col cols="6" md="2">
				<v-autocomplete
					v-model="invoiceData.accountId"
					:label="$t('inputs.account')"
					:items="configs.accounts.filter(c => c.type === 2)"
					item-value="id"
					:item-text="item => item.accounting_no + '-'+ item.name"
					outlined
					dense
					hide-details
					:disabled="currentInvoice.is_posted"
					:rules="[rules.required]"
					@change="$event ? (() => { isUpdateInvoice = true; submitInvoice(); })() : null"
				>
					<template v-slot:selection="{ item }">
						{{ item.accounting_no +'-'+ item.name }}
					</template>
					<template v-slot:item="{ item }">
						{{ item.accounting_no + '-'+ item.name }}
						<v-spacer></v-spacer>
						<bee-advanced-money-label
							:with-color="true"
							:value="item?.account_statement_summary"
							:show-zeros-values="true"
						></bee-advanced-money-label>
					</template>
				</v-autocomplete>
			</v-col>

			<!-- date -->
			<v-col cols="6" md="2">
				<bee-date-input
					v-model="invoiceData.date"
					:label="$t('inputs.date')"
					dense
					hide-details
					prepend-icon="mdi-calendar"
					:user-menu-picker="!currentInvoice.is_posted"
					input-format-order="DD-MM-YYYY"
					class="w-100"
					:disabled="currentInvoice.is_posted"
					@change="isUpdateInvoice = true; submitInvoice();"
				></bee-date-input>
			</v-col>

			<!-- warehouse -->
			<v-col cols="6" md="2">
				<v-autocomplete
					v-model="tableInputs.warehouse"
					:items="userTypePermission === userType.warehouseKeeper && multipleWarehouses ? warehousesForKeeper : warehouses"
					item-text="name"
					item-value="id"
					outlined
					dense
					:label="$t('inputs.warehouse')"
					hide-details
					:disabled="currentInvoice.is_posted"
				/>
			</v-col>

			<!-- ref doc -->
			<v-col cols="6" md="1">
				<v-text-field
					v-model="invoiceData.refDoc"
					outlined
					dense
					:label="$t('inputs.ref-doc')"
					hide-details
					:disabled="currentInvoice.is_posted"
					@change="isUpdateInvoice = true; submitInvoice();"
				/>
			</v-col>

			<v-spacer />

			<v-col cols="auto" class="d-flex justify-lg-end py-1">
				<router-link style="text-decoration: none;" :to="{name: 'invoices', query: { type: 4 }}">
					<bee-progress-circular
						:size="60"
						:width="6"
						:title="bond_posting_ratio_caption"
						:value="bond_posting_ratio"
						:tooltip="bond_posting_ratio_tooltip"
					></bee-progress-circular>
				</router-link>
			</v-col>

			<!-- net invoice -->
			<v-col
				v-if="!isWarehousePermission"
				cols="auto"
				class="pb-lg-0 pt-lg-1 d-flex flex-column"
				align-self="stretch"
			>
				<bee-outlined-header-info
					:head="$t('invoices.net-invoice')"
					style="line-height: 1.5rem;"
					border-color="transparent"
					class="pa-lg-0"
					:column="$vuetify.breakpoint.lgAndUp"
				>
					<template  v-slot:content="{}">
						<bee-money-label number="" type="credit" :withColor="true" empty-state="_"></bee-money-label>
					</template>
				</bee-outlined-header-info>
			</v-col>

			<!-- save button -->
			<v-col
				v-if="!isWarehousePermission"
				cols="auto"
				class="d-flex flex-column align-items-center py-0 ps-0"
				:align-self="$vuetify.breakpoint.mdAndDown ? 'center' : 'stretch'"
			>
				<v-btn
					:color="!currentInvoice.is_posted ? 'success' : 'edit-color white--text'"
					:height="$vuetify.breakpoint.mdAndUp ? '100%' : 35"
					:class="{'px-6 py-3': $vuetify.breakpoint.mdAndUp}"
					:disabled="!$route.params.id || loading || isViewer"
					@click="changeInvoiceState"
				>
					<v-icon size="20" class="me-1">
						{{!currentInvoice.is_posted ? 'mdi-check' : 'mdi-pencil'}}
					</v-icon>
					{{!currentInvoice.is_posted ? $t('buttons.save') : $t('buttons.edit')}}
				</v-btn>
			</v-col>

			<!-- post state -->
			<v-col 
				cols="12"
				sm="12"
				md="auto"
				class="py-0 px-0"
				align-self="stretch"
			>
				<div
					class="d-flex align-center justify-center px-4 white--text rounded-lg h-100"
					:class="{
						'py-2': $vuetify.breakpoint.lgAndUp,
						'danger-color': !currentInvoice.is_posted,
						'success': currentInvoice.is_posted
					}"
				>
					{{!currentInvoice.is_posted ? $t('invoices.non-posted') : $t('invoices.posted')}}
				</div>
			</v-col>
		</v-row>
		
		<!-- table -->
		<bee-handy-smart-table
			ref="table"
			v-model="tableInputs"
			:headers="headers"
			:items="entries"
			:items-per-page="20"
			pagination-on-scroll
			:loading="loading"
			:data-table-props="{ disableSort: true }"
			fixed-header
			hide-no-data-area
			dense
			:height="$vuetify.breakpoint.lgAndUp ? $vuetify.breakpoint.height - 290 :'65vh'"
			stay-in-input-fields
			:show-add-row="!currentInvoice.is_posted && Boolean($route.params.id) && !isViewer"
			:editable-rows="!submitLoading && !loading && !isViewer"
			hide-inputs-details
			:context-menu-items="contextMenuItems"
			disable-mobile-context-menu
			:scroll-to-row-expression="(item) => item.id === entryId || item.id === entries[entries.length - 1].id"
			zebra
			hot-inputs
			:validation-messages.sync="validationMessages"
			:shown-rows-text="$t('shown-rows-text')"
			:disable-context-menu="submitLoading || loading || currentInvoice.is_posted || isViewer"
			:disable-input-fields="submitLoading || currentInvoice.is_posted"
			@edit-cancelled="entryId = null"
			@context-menu-edit="setRowIndex"
			@context-menu-delete="({ item }) => { deleteEntryId = item.id; dialogDeleteAccountingEntry = true }"
			@keypress.enter="saveEntry"
			@reached-last-input="saveEntry"
		>
			<!-- warehouse -->
			<template v-slot:input.warehouse="{ on, attr }">
				<v-autocomplete
					v-on="on"
					v-bind="attr"
					:items="userTypePermission === userType.warehouseKeeper && multipleWarehouses ? warehousesForKeeper : warehouses"
					item-text="name"
					:item-value="userTypePermission === userType.warehouseKeeper && multipleWarehouses ? 'warehouse_id' : 'id'"
				/>
			</template>

			<!-- materialId -->
			<template v-slot:input.materialId="{ on, attr }">
				<v-autocomplete
					v-on="on"
					v-bind="attr"
					:items="userTypePermission === userType.warehouseKeeper && multipleWarehouses ? items.filter(c => c.warehouse_id === tableInputs.warehouse && c.type === 2) : items.filter(c => c.type === 2)"
					item-text="name"
					item-value="id"
				>
					<template v-slot:item="{item}">
						{{item.no + '-'+ item.name }}
						<v-spacer></v-spacer>
						<bee-material-amount-label
							:with-color="true"
							:show-zeros-values="true"
							:value="item.balance"
						></bee-material-amount-label>
					</template>
				</v-autocomplete>
			</template>

			<!-- actions -->
			<template v-slot:item.actions="{ index }">
				<tooltip :text="$t('buttons.delete')" content-class="me-2">
					<v-btn
						depressed
						height="30"
						width="30"
						min-width="30"
						color="danger-color white--text"
						class="px-1 mx-1"
						@click="deleteEntryId = item.id; dialogDeleteAccountingEntry = true"
					>
						<v-icon size="22">mdi-delete</v-icon>
					</v-btn>
				</tooltip>
				
				<!-- edit -->
				<tooltip :text="$t('buttons.edit')" content-class="me-2">
					<v-btn
						depressed
						height="30"
						width="30"
						min-width="30"
						color="edit-color white--text"
						class="px-1"
						@click="$refs.table.activeEditForRow(index); setRowIndex({ index })"
					>
						<v-icon size="22"> mdi-pencil </v-icon>
					</v-btn>
				</tooltip>
			</template>

			<!-- hint -->
			<template v-slot:no-input.hint>
				<v-menu bottom v-if="validationMessages.length > 0">
					<template v-slot:activator="{ on, attr }">
						<v-icon
							v-on="on"
							v-bind="attr"
							color="danger-color"
							size="20"
						>mdi-information</v-icon>
					</template>

					<v-list>
						<v-list-item
							v-for="(message, i) in inputsValidationMessages"
							:key="i"
							dense
							class="px-2"
						>
							<v-list-item-icon class="me-0 mb-0">
								<v-icon color="danger-color" x-small>mdi-circle-half</v-icon>
							</v-list-item-icon>
							<v-list-item-title class="danger-color--text">
								{{message}}
							</v-list-item-title>
						</v-list-item>
					</v-list>
				</v-menu>
			</template>
		</bee-handy-smart-table>

		<!-- footer -->
		<v-footer app height="60" class="px-3">
			<v-row class="ma-0" align="center">            
				<v-col lg="4" md="auto" class="py-1">
					<v-text-field
						v-model="invoiceData.notes"
                    	:label="$t('InvoiceSalePage.notes')"
						outlined
						hide-details
						dense
						:disabled="currentInvoice.is_posted"
						@change="isUpdateInvoice = true; submitInvoice();"
                	></v-text-field>
				</v-col>
				<v-spacer></v-spacer>
				<!--  -->
				<v-col cols="auto" class="d-flex align-center py-1">
					<bee-outlined-header-info
						:head="$t('invoices.total')"
						border-color="transparent"
						class="pa-lg-0 me-4"
					>
						<template v-slot:content="{}">
							<bee-money-label
								:number="
									currentInvoice.invoice_entries
										? currentInvoice.invoice_entries
											.map(c => c.calculated_total_amount.amount || 0 )
											.reduce((a, b) => a + b, 0)
										: null
								"
								type="credit"
								:withColor="true"
								empty-state="_"
							></bee-money-label>
						</template>
					</bee-outlined-header-info>

					<bee-outlined-header-info
						:head="$t('invoices.net-invoice')"
						border-color="transparent"
						class="pa-lg-0"
					>
						<template v-slot:content="{}">
							<bee-money-label
								:number="currentInvoice.total_net ? currentInvoice.total_net.amount : null"
								type="credit"
								:withColor="true"
								empty-state="_"
							></bee-money-label>
						</template>
					</bee-outlined-header-info>
				</v-col>
			</v-row>
		</v-footer>
	</v-container>
</template>

<script>
import rules from '@/validation'
import { mapGetters, mapState } from 'vuex'
import moment from 'moment'
import { userType, invoiceType, bondType } from '@/helpers/enums';
import { multipleWarehouses, isMangeUnitAndRegionAndPatchAndLot, separationOfInvoices } from '@/configs/global'
export default {
	data() {
		return {
			submitLoading: false,
			loading: false,

			dialog: false,
			
			validationMessages: [],

			// invoice data
			currentInvoice: {},
			invoiceNo: null,
			invoiceData: {
				accountId: null,
				date: null,
				refDoc: null,
				notes: null,
				refundedFrom: null,
			},
			isUpdateInvoice: false,
			
			submitData: {
				date: null,
				account: null,
				refDoc: null,
				notes: null,
				refundedFrom: []
			},

			// entires
			entryId: null,
			entries: [],
			tableInputs: {},
			
			rowEditIndex: null,
			deleteDialog: false,

			deleteEntryId: null,
			dialogDeleteAccountingEntry: false,

			rules: rules.rules,
			userType,
			invoiceType,
			multipleWarehouses,
			isMangeUnitAndRegionAndPatchAndLot,
			separationOfInvoices
		}
	},

	watch: {
		'$route.params.id'(val) {
			if (val) {
				this.$refs.table.resetInputs({
					warehouse: this.configs.warehouse.id
				});
				this.fetchInvoice();
			}
		},

		dialog(val) {
			if (!val) {
				this.$refs.form.reset();
			} else {
				this.submitData.date = moment().format('YYYY-MM-DD');
			}
		}
	},

	computed: {
		...mapState({
			currentFinancialFile: state => state.financialFiles.current_financial_file,
			materials: state => state.materials.materials,
			saleInvoices: state => state.invoices.simplified_sale_invoices,
			saleRefundInvoices: state => state.invoices.saleRefundInvoices,
			warehouses: state => state.warehouses.warehouses,
			configs: state => state.configs.configs,
			userTypePermission: state => state.permissions.user_type,
			numOfNotPostedInvoices: state => state.configs.configs.num_of_not_posted_invoices.filter(c => c.invoice_type === invoiceType.saleRefund),
			numOfTotalSaleRefundInvoices: state => state.configs.configs.num_of_total_invoices.filter(c => c.invoice_type === invoiceType.saleRefund),
			warehousesForKeeper: state => state.warehouses.warehousesForKeeper
		}),

		...mapGetters({
			getMaterialById: 'materials/getMaterialById',
			getWarehouseById: 'warehouses/getById',
			isViewer: 'auth/isViewer'

		}),

		items() {
			const items = this.materials.filter(c => c.warehouse_id === this.tableInputs.warehouse);
			return this.tableInputs.warehouse ? items : this.materials;
		},

		headers() {
			const headers = [
				{
					text: this.$t('headers.no'),
					name: 'no',
					value: 'no',
					noInput: true
				},
				{
					text: this.$t('headers.warehouse'),
					name: 'warehouse',
					value: 'warehouseId',
					setValueExpr: (val) => this.getWarehouseById(val).name
				},
				{
					text: this.$t('headers.material'),
					name: 'materialId',
					value: 'materialId',
					setValueExpr: (val) => this.getMaterialById(val).name,
					inputRules: [this.rules.required]
				},
				{
					text: this.$t('headers.quantity'),
					name: 'quantity',
					value: 'quantity',
					type: 'number',
					inputRules: [this.rules.required]
				},
				{
					text: this.$t('headers.count'),
					name: 'count',
					value: 'count',
					type: 'number',
				},
				{
					text: this.$t('headers.notes'),
					name: 'notes',
					value: 'notes'
				},
				{
					text: '',
					name: 'hint',
					value: 'hint',
					width: 40,
					noInput: true,
					noNotAvailableText: true
				}
			]
			if(isMangeUnitAndRegionAndPatchAndLot) {
				headers.splice(5,0, 
				{
					text: 'رقم الباتش',
					name: 'patchNumber',
					value: 'patchNumber',
				},
				{
					text: 'رقم اللوت',
					name: 'lotNumber',
					value: 'lotNumber',
				},
				{
					text: 'تاريخ الانتهاء',
					name: 'endDate',
					value: 'endDate',
					type: 'date'
				})
			}
			if( (this.userTypePermission === userType.accountant) ||( this.userTypePermission === userType.viewer)) {
				headers.splice(5,0, {
					text: this.$t('headers.price'),
					name: 'price',
					value: 'price',
					type: 'number',
					inputRules: [this.rules.required]
				},
				{
					text: this.$t('headers.net-amount'),
					name: 'calculatedTotalAmount',
					value: 'calculated_total_amount',
					disableInput: true,
					setInputExpr: (inputs) => inputs.quantity * inputs.price,
				},)
			}
			return headers
		},

		contextMenuItems() {
			return [
				{
					title: this.$t('buttons.edit'),
					event: 'edit',
				},
				{
					title: this.$t('buttons.delete'),
					event: 'delete',
				},
			]
		},

		inputsValidationMessages() {
			const messages = [];
			const inputs = this.tableInputs;
			if (!inputs.materialId) {
				messages.push(this.$t('table-messages.material-required'));
			}
			if (!inputs.quantity) {
				messages.push(this.$t('table-messages.quantity-required'));
			}
			if (!inputs.price) {
				messages.push(this.$t('table-messages.price-required'));
			}

			return messages
		},

		isWarehousePermission() {
			return (this.userTypePermission === userType.warehouseKeeper && separationOfInvoices) || (this.userTypePermission === userType.warehouseManger && separationOfInvoices)
    	},
		
		
		bond_posting_ratio() {
			return this.numOfTotalSaleRefundInvoices.length
				? 100 -
				((this.numOfNotPostedInvoices.length ? this.numOfNotPostedInvoices[0].count : 0) * 100) /
					this.numOfTotalSaleRefundInvoices[0].count
			: 100   
    	},

		bond_posting_ratio_tooltip() {
			return this.numOfNotPostedInvoices.length
				? `${this.numOfNotPostedInvoices[0].count} ${this.$t('unposted-invoices')}`
				: this.$t('unposted-invoices');
		},
		bond_posting_ratio_caption() {
			return `${this.numOfNotPostedInvoices.length ? this.numOfNotPostedInvoices[0].count : 0}/${this.numOfTotalSaleRefundInvoices.length ? this.numOfTotalSaleRefundInvoices[0].count : 0}`;
		}
	},

	methods: {
		invoiceNoIsChanged() {
			const newInvoice = this.saleRefundInvoices.find(c => c.no === this.invoiceNo);
			if (newInvoice && newInvoice.id !== this.$route.params.id) {
				this.$router.replace({
					name: this.$route.name,
					params: { id: newInvoice.id }
				})
			}
		},

		submitInvoice() {
			if (!this.loading && !this.submitLoading && (this.isUpdateInvoice || this.$refs.form.validate())) {
				this.submitLoading = true;
				const actionName = !this.isUpdateInvoice ? 'create' : 'update';
				this.$store.dispatch(`invoices/${actionName}`, {
					invoiceId: this.$route.params.id,
					invoiceType: invoiceType.saleRefund,
					invoice: {
						external_account_id:
							!this.isUpdateInvoice
								? this.submitData.account
								: this.invoiceData.accountId,
						date:
							!this.isUpdateInvoice
								? this.submitData.date
								: this.invoiceData.date,
						doc_no:
							!this.isUpdateInvoice
								? this.submitData.refDoc
								: this.invoiceData.refDoc,
						notes:
							!this.isUpdateInvoice
								? this.submitData.notes
								: this.invoiceData.notes,
						refunded_from_invoices:
							!this.isUpdateInvoice
								? this.submitData.refundedFrom
								: this.invoiceData.refunded_from_invoices,
					}
				}).then(async () => {
					await this.$store.dispatch('invoices/fetchAll', invoiceType.saleRefund)
						.then((data) => {
							if (data[data.length - 1].id !== +this.$route.params.id && !this.isUpdateInvoice) {
								this.$router.replace({
									name: this.$route.name,
									params: { id: data[data.length - 1].id }
								})
							} else if (this.isUpdateInvoice) {
								this.fetchInvoice();
							}
						})
						.finally(() => {
							this.dialog = false;
							this.submitLoading = false;	
						})
					
					if (this.isUpdateInvoice) this.isUpdateInvoice = false;
				}).catch(() => {
					this.submitLoading = false;
				})
			}
		},

		changeInvoiceState() {
			const actionName = !this.currentInvoice.is_posted ? 'updateToPosted' : 'updateToNotPosted'
			
			this.loading = true;
			this.$store.dispatch(`invoices/${actionName}`, {
				invoiceId: this.$route.params.id,
				invoiceType: invoiceType.saleRefund
			}).then(() => {
				this.fetchInvoice();

				// update journals, bonds and configs
				this.$store.dispatch('bonds/fetchAll', { bondType: bondType.accounting });
				this.$store.dispatch('configs/fetchAll');
			}).finally(() => {
				this.loading = false;
			})
		},

		saveEntry() {
			if (this.$refs.table.validate()) {
				const actionName = this.entryId ? 'updateEntry' : 'createEntry';
					const material = this.getMaterialById(this.tableInputs.materialId);
					const entry = this.entries.find(c => c.id === this.entryId);
					this.$store.dispatch(`invoices/${actionName}`, {
						invoiceType: invoiceType.saleRefund,
						invoiceId: this.$route.params.id,
						invoiceEntry: {
							id: this.entryId || 0,
							invoice_id: this.$route.params.id,
							material_id: material.id,
							no: entry && entry.no ? entry.no : 0,
							quantity: {
								quantity: this.tableInputs.quantity,
								unit_size_id: material.unit_size_id
							},
							count: this.tableInputs.count || 0,
							price: {
								amount: this.tableInputs.price ? this.tableInputs.price : 0,
								currency_id: this.currentFinancialFile.default_currency_id
							},
							calculated_total_amount: {
								amount: this.tableInputs.price ? this.tableInputs.quantity * this.tableInputs.price : 0,
								currency_id: this.currentFinancialFile.default_currency_id
							},
							notes: this.tableInputs.notes,
						}
					}).then(() => {
						const initTable = () => {
							this.$nextTick(() => {
								this.$refs.table.scrollToRow();
								this.$refs.table.resetInputs({
									warehouse: this.configs.warehouse.id
								});
							})
						}
						
						if (this.entryId) {
							this.entryId = null;
							const nextRowIndex = this.$refs.table.editNextRow();
							if (nextRowIndex !== -1) {
								this.entryId = this.entries[nextRowIndex].id;
								this.$refs.table.scrollToRow();
							} else {
								initTable();
							}
						} else {
							initTable();
						}

						this.$nextTick(() => {
							if (this.entryId) setTimeout(this.$refs.table.resetInputsFocus, 100);
							else this.$refs.table.resetInputsFocus();
						})

						this.fetchInvoice();
					})
			}
		},

		setRowIndex(rowData) {
			this.rowEditIndex = rowData.index;
			this.entryId = rowData.item.id;
			this.$nextTick(() => {
				this.$refs.table.resetInputsFocus()
			})
		},

		fetchInvoice() {
			this.loading = true;
			this.$store.dispatch('invoices/fetch', {
				invoiceId: this.$route.params.id,
				invoiceType: invoiceType.saleRefund
			}).then((data) => {
				this.currentInvoice = data;

				this.invoiceNo = data.no;
				this.invoiceData.accountId = data.external_account_id;
				this.invoiceData.date = data.date.slice(0, 10);
				this.invoiceData.refDoc = data.doc_no;
				this.invoiceData.notes = data.notes;
				this.invoiceData.refundedFrom = data.refunded_from_invoices;

				this.entries = data.invoice_entries.map(c => ({
					...c,
					no: c.no,
					warehouseId: this.getMaterialById(c.material_id).warehouse_id,
					materialId: c.material_id,
					quantity: c.quantity.quantity,
					count: c.count,
					price: c.price.amount,
					calculated_total_amount: c.calculated_total_amount.amount,
					notes: c.notes,
				}))
			}).finally(() => {
				this.loading = false;
			})
		},

		fetchInvoices() {
			return this.$store.dispatch('invoices/fetchAll', invoiceType.saleRefund).then((data) => {
				if (data.length && data[data.length - 1].id !== +this.$route.params.id) {
					this.$router.replace({
						name: this.$route.name,
						params: { id: data[data.length - 1].id }
					})
				} else if (data.length && data[data.length - 1].id === +this.$route.params.id) {
					this.fetchInvoice();
				} else if (!data.length) {
					this.$router.replace({ name: this.$route.name })
					this.invoiceNo = null;
					this.invoiceData.accountId = null;
					this.invoiceData.date = null;
					this.invoiceData.notes = null;
					this.invoiceData.refDoc = null;
					this.invoiceData.refundedFrom = [];
					this.loading = false;
				}
			})
		},

		deleteEntry() {
			this.loading = true;
			this.$store.dispatch('invoices/deleteEntry', {
				invoiceId: this.$route.params.id,
				invoiceType: invoiceType.saleRefund,
				invoiceEntryId: this.deleteEntryId
			}).then(() => {
				this.deleteEntryId = null;
				this.fetchInvoice();
			})
			this.$refs.table.resetEdit();
		},

		deleteInvoice() {
			this.loading = true;
			this.$store.dispatch('invoices/delete', {
				invoiceId: this.$route.params.id,
				invoiceType: invoiceType.saleRefund
			}).then(() => {
				this.fetchInvoices();
			}).catch(() => {
				this.loading = false;
			})
		}
	},

	created() {
		this.loading = true;
		Promise.all([
			this.$store.dispatch('materials/fetchAll'),
			this.$store.dispatch('warehouses/fetchAll'),
			this.$store.dispatch('invoices/fetchAll', invoiceType.sale),
			this.fetchInvoices()
		]).finally(() => {
			this.tableInputs.warehouse = this.configs.warehouse.id;
			this.loading = false;
		});
	}
}
</script>
